import React, { useState, useEffect } from "react";
import RoleCount from "../components/usermanagement/RoleCount";
import BrowseByDepartment from "../components/usermanagement/BrowseByDepartment";
import AdministratorAccounts from "../components/usermanagement/AdministratorAccounts";
import { UserState } from "../context/UserContext";
import { useDarkMode } from "../context/darkModeContext";
import { api } from "../utils/axios-instance";
import toast from "react-hot-toast";
import { useStorageStore } from "../store/storageStore";
import { isStarterPlan } from "../utils/planAccess";

const DownloadCloudIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path
      d="M3 7a3 3 0 113-3 3.5 3.5 0 016.5 1.5A2.5 2.5 0 0112 11H4a3 3 0 01-1-4z"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinejoin="round"
    />
    <path
      d="M7 7v4m-1.5-1.5L7 11l1.5-1.5"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const UserManagementPage = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { darkMode } = useDarkMode();
  const orgPlan = useStorageStore((s) => s.plan);

  // Shared dept filter — lifted here so RoleCount+BrowseByDept card controls the Accounts table
  const [activeDept, setActiveDept] = useState("");

  const sanitizeCSV = (value) => {
    const str = String(value ?? "");

    return /^[=+\-@]/.test(str) ? `'${str}` : str;
  };

  const downloadData = async () => {
    setLoading(true);
    try {
      const res = await api.get("/users/list_users");
      if (res.status !== 200)
        throw new Error(res.data || "Something went wrong");
      const listUsers = res.data?.map((user) => {
        let { metadata, dept, profilePictureUrl, id, org, ...rest } = user;
        return rest;
      });
      const allKeys = [...new Set(listUsers.flatMap(Object.keys))];
      const csvRows = [allKeys.join(",").toUpperCase()];
      listUsers.forEach((row) => {
        const values = allKeys.map((key) => {
          const v = sanitizeCSV(row[key] !== undefined ? row[key] : "").replace(
            /"/g,
            '\\"'
          );
          return `"${v}"`;
        });
        csvRows.push(values.join(","));
      });
      const blob = new Blob([csvRows.join("\n")], {
        type: "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");

      link.href = url;
      link.download = "user_info.csv";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    } catch (error) {
      setError(error?.message || "Something went wrong. Please try again!");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!error) return;

    toast.error(error);
    setError("");
  }, [error]);

  // Defense-in-depth: App.js's <PlanGate> is the primary guard for this
  // route, but keep a fallback here too in case this page is ever rendered
  // some other way (e.g. a future direct <Link> that skips the route
  // wrapper). Starter-plan orgs get an upgrade prompt instead of the page.
  if (isStarterPlan(orgPlan)) {
    return (
      <div
        style={{
          padding: "20px 24px 40px",
          background: darkMode ? "#1f2937" : "#f0f2f8",
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            background: darkMode ? "#111827" : "#fff",
            border: darkMode ? "1px solid #374151" : "none",
            borderRadius: 12,
            padding: "36px 32px",
            maxWidth: 420,
            textAlign: "center",
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
          }}
        >
          <h2
            style={{
              fontSize: 17,
              fontWeight: 700,
              color: darkMode ? "#f9fafb" : "#1a1a2e",
              margin: "0 0 8px",
            }}
          >
            User Management isn't on the Starter plan
          </h2>
          <p
            style={{
              fontSize: 13.5,
              color: darkMode ? "#9ca3af" : "#6b7280",
              margin: "0 0 20px",
              lineHeight: 1.6,
            }}
          >
            Upgrade to Basic or Pro to invite teammates, assign roles, and
            manage admin accounts.
          </p>
          <a
            href="/home-pricing"
            style={{
              display: "inline-block",
              background: "#5E5ADB",
              color: "#fff",
              padding: "9px 20px",
              borderRadius: 8,
              fontSize: 13.5,
              fontWeight: 600,
              textDecoration: "none",
            }}
          >
            View plans
          </a>
        </div>
      </div>
    );
  }

  return (
    <UserState>
      <div
        style={{
          padding: "20px 24px 40px",
          background: darkMode ? "#1f2937" : "#f0f2f8",
          minHeight: "100vh",
        }}
      >
        {/* ── Single card: Administrative roles + Browse by Department ── */}
        <div
          style={{
            background: darkMode ? "#111827" : "#fff",
            border: darkMode ? "1px solid #374151" : "none",
            borderRadius: 12,
            padding: "22px 24px",
            marginBottom: 18,
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
          }}
        >
          <RoleCount />
          <BrowseByDepartment
            activeDept={activeDept}
            onSelectDept={setActiveDept}
          />
        </div>

        {/* ── Administrator Accounts section ── */}
        <div
          style={{
            background: darkMode ? "#111827" : "#fff",
            border: darkMode ? "1px solid #374151" : "none",
            borderRadius: 12,
            padding: "22px 24px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              marginBottom: 16,
            }}
          >
            <div>
              <h2
                style={{
                  fontSize: 17,
                  fontWeight: 700,
                  color: darkMode ? "#f9fafb" : "#1a1a2e",
                  margin: 0,
                  letterSpacing: "-0.2px",
                }}
              >
                Administrator Accounts
              </h2>
              <p
                style={{
                  fontSize: 12.5,
                  color: darkMode ? "#9ca3af" : "#6b7280",
                  margin: "5px 0 0",
                  lineHeight: 1.55,
                }}
              >
                Find all of your company's administrator accounts and their
                associated roles.
              </p>
            </div>
            <button
              onClick={downloadData}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 7,
                border: darkMode ? "1px solid #374151" : "1px solid #e5e7eb",

                color: darkMode ? "#f3f4f6" : "#374151",

                background: darkMode ? "#1f2937" : "#fff",
                padding: "7px 14px",
                borderRadius: 8,
                fontSize: 13,
                fontFamily: "inherit",
                cursor: "pointer",
                transition: "border-color 0.12s, background 0.12s",
                flexShrink: 0,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#c7d2fe";
                e.currentTarget.style.background = "#fafbff";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#e5e7eb";
                e.currentTarget.style.background = "#fff";
              }}
            >
              <DownloadCloudIcon />
              {loading ? "Processing..." : "Export"}
            </button>
          </div>

          <AdministratorAccounts activeDept={activeDept} />
        </div>
      </div>
    </UserState>
  );
};

export default UserManagementPage;
